import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { SolarUsageData } from './types';
import { getChartColor, dashboardColors } from './colors';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  useColorModeValue,
  useBreakpointValue
} from '@chakra-ui/react';
import { formatNumber } from './utils';

interface PenggunaanSolarProps {
  data: SolarUsageData[];
}

const CustomTooltip = ({ active, payload }: any) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <Box 
        bg={bgColor} 
        p={3} 
        borderWidth={1} 
        borderColor={borderColor} 
        borderRadius="md" 
        shadow="md"
      >
        <Box fontWeight="semibold">{`${data.name}`}</Box>
        <Box fontSize="sm">{`Konsumsi: ${formatNumber(data.value)} ${data.uom}`}</Box>
        <Box fontSize="sm">{`Persentase: ${data.percentage}%`}</Box>
      </Box>
    );
  }
  return null;
};

const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      style={{ fontSize: '0.75rem', fontWeight: 'bold' }}
    >
      {percentage >= 5 ? `${percentage}%` : ''}
    </text>
  );
};

const LegendFormatter = (value: string, entry: any) => (
  <span style={{ color: entry.color }}>
    {value}: {entry.payload?.value?.toLocaleString() || 0} L
  </span>
);

const PenggunaanSolar: React.FC<PenggunaanSolarProps> = ({ data }) => {
  // Transform data for chart
  const total = data.reduce((sum, item) => sum + item.qty_done, 0);
  
  const pieData = data.map((item, index) => ({
    name: item.estate,
    value: item.qty_done,
    uom: item.uom,
    percentage: Math.round((item.qty_done / total) * 100),
    color: getChartColor(index)
  }));

  // Calculate MTD (mock data for demonstration)
  // const mtdMultiplier = 8; // Assuming 8 days in current month

  const totalDaily = data.reduce((sum, item) => sum + item.qty_done, 0);
  // const totalMTD = totalDaily * mtdMultiplier;
  
  // Define common colors at the top level
  const bgCard = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const listItemBg = useColorModeValue('gray.50', 'gray.800');

  const supplierLabelFontSize = useBreakpointValue({ 
    base: 12,       // mobile - smaller
    md: 12,         // tablet
    lg: 10,        // laptop
    xl: 12,        // desktop
    '2xl': 14      // large desktop
  }) || 10;

  const rowHeight = useBreakpointValue({ 
      base: '100%',     // mobile - smaller
      md: '100%',       // tablet
      lg: '100%',       // laptop
      xl: '100%',       // desktop
      '2xl': '90%'    // large desktop
  });

  const outerRadius = useBreakpointValue({ 
      base: 70,        // mobile - smaller
      md: 120,          // tablet
      lg: 40,          // laptop
      xl: 60,          // desktop
      '2xl': 90       // large desktop
  }) || 70;

  const innerRadius = useBreakpointValue({ 
      base: 20,        // mobile - smaller
      md: 30,          // tablet
      lg: 45,          // laptop
      xl: 35,          // desktop
      '2xl': 60        // large desktop
  }) || 40;

  const labelFontSize = useBreakpointValue({ 
      base: 8,         // mobile - smaller
      md: 10,          // tablet
      lg: 12,          // laptop
      xl: 12,          // desktop
      '2xl': 14        // large desktop
  }) || 12;

  const chartMinHeight = useBreakpointValue({
    base: '330px', // mobile
    md: '320px',   // tablet
    lg: '300px',   // laptop
    xl: '220px',   // desktop
    '2xl': '350px' // large desktop
  }) || '320px';

  return (
    <Box bg={bgCard} borderRadius="lg" p={4} h="100%" display="flex" flexDirection="column">
      <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px">
        PENGGUNAAN SOLAR
      </Heading>
      
      {/* Summary Cards */}
      {/* <SimpleGrid columns={2} spacing={4} mb={4}>
        <Box bg={useColorModeValue('blue.50', 'blue.900')} borderWidth={1} borderColor={useColorModeValue('blue.200', 'blue.700')} borderRadius="lg" p={3}>
          <Text color="blue.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Harian
          </Text>
          <Text color={useColorModeValue('blue.900', 'blue.200')} fontSize="xl" fontWeight="bold">
            {totalDaily.toLocaleString()}
          </Text>
          <Text color="blue.600" fontSize="xs">Liter</Text>
        </Box>
        
        <Box bg={useColorModeValue('indigo.50', 'indigo.900')} borderWidth={1} borderColor={useColorModeValue('indigo.200', 'indigo.700')} borderRadius="lg" p={3}>
          <Text color="indigo.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            MTD
          </Text>
          <Text color={useColorModeValue('indigo.900', 'indigo.200')} fontSize="xl" fontWeight="bold">
            {totalMTD.toLocaleString()}
          </Text>
          <Text color="indigo.600" fontSize="xs">Liter</Text>
        </Box>
      </SimpleGrid> */}

      <Flex alignContent="center" justify="center" gap={6} mb={0} mt={0}>
        <Text fontSize="sm" color={dashboardColors.info} fontWeight="semibold" textAlign="center">
          Pemakaian Harian: <Box as="span" fontWeight="bold" color={dashboardColors.info} display="inline">{totalDaily ? `${formatNumber(totalDaily)} L` : '-'}</Box>
        </Text>
        {/* <Text fontSize="sm" color="purple.700" fontWeight="semibold" textAlign="center">
          MTD: <Box as="span" fontWeight="bold" color="purple.900" display="inline">{totalMTD ? totalMTD.toLocaleString() : '-'}</Box>
        </Text> */}
      </Flex>

      {/* Chart */}
      {/* <Box minH="0" height="full"> */}
      <Box minH={chartMinHeight} h={chartMinHeight} w="100%">
        <ResponsiveContainer width="100%" height={rowHeight}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={(props) => {
                const { value, name } = props;
                const total = pieData.reduce((sum, entry) => sum + entry.value, 0);
                const percent = total ? ((value / total) * 100).toFixed(1) : 0;
                return `${name}\n${formatNumber(value)}L`;
              }}
              outerRadius={outerRadius}
              // innerRadius={80}
              fill="#8884d8"
              dataKey="value"
              style={{ fontSize: supplierLabelFontSize }}
            >
              {pieData.map((entry) => (
                <Cell 
                  key={`cell-${entry.name}`} 
                  fill={entry.color} 
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {/* <Legend 
              // verticalAlign="bottom" 
              height={50}
              formatter={LegendFormatter}
            /> */}
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Detail List */}
      {/* <Box mt={4}>
        <Text fontSize="sm" fontWeight="semibold" color={useColorModeValue('gray.700', 'gray.300')} mb={2}>
          Detail per Estate
        </Text>
        <SimpleGrid columns={1} spacing={2} fontSize="xs">
          {data.map((item, index) => (
            <Flex 
              key={`${item.estate}-${item.date}`}
              justify="space-between" 
              align="center" 
              p={2} 
              bg={listItemBg} 
              borderRadius="md"
            >
              <Flex align="center">
                <Box 
                  width="3" 
                  height="3" 
                  borderRadius="full" 
                  mr={2}
                  bg={getChartColor(index)}
                />
                <Text fontWeight="medium">{item.estate}</Text>
              </Flex>
              <Text fontFamily="mono">
                {item.qty_done.toLocaleString()} {item.uom}
              </Text>
            </Flex>
          ))}
        </SimpleGrid>
      </Box> */}
    </Box>
  );
};

export default PenggunaanSolar;
